import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useDeploy } from '../contexts/DeployContext';

function formatDate(dateString) {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch {
    return dateString;
  }
}

function ConfigsTable({ selectedConfig, onSelectConfig, refreshKey = 0 }) {
  const { deployApi } = useDeploy();

  const [configs, setConfigs] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const fetchConfigs = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await deployApi.listFeedConfigs();
      const items = res?.feed_configs ?? [];
      setConfigs(items);
    } catch (err) {
      setListError(err?.message ?? 'Failed to load Feed Configs');
      setConfigs([]);
    } finally {
      setListLoading(false);
    }
  }, [deployApi]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs, refreshKey]);

  if (listLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (listError) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography color="error" variant="body2">{listError}</Typography>
      </Box>
    );
  }

  if (configs.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No Feed Configs yet. Create one in the right column.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', border: 'none' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell>Public</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {configs.map((d) => (
            <TableRow
              key={d.config_id}
              hover
              onClick={() => onSelectConfig(d)}
              selected={selectedConfig?.config_id === d.config_id}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>{d.config_id}</TableCell>
              <TableCell>{d.name || `Feed Config ${d.config_id}`}</TableCell>
              <TableCell>{formatDate(d.created_at)}</TableCell>
              <TableCell>{formatDate(d.updated_at)}</TableCell>
              <TableCell>{d.is_public ? 'Yes' : 'No'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ConfigsTable;
